import type { StoreUndoRefusal } from "src/engine/game/GameStore";
import type { RewindRefusal } from "src/engine/game/rewind";
import type { SacrificeError } from "src/engine/rules/campaign/sacrifice";
import type { UpgradeError } from "src/engine/rules/industry/upgrades";
import type { BeltPlacementError } from "src/engine/rules/industry/beltParts";
import type { BuildError } from "src/engine/rules/industry/structures";
import type { TrainError } from "src/engine/rules/industry/trains";
import { RULES } from "src/engine/rules/rules";

/**
 * The refusals that belong to no one mechanic, said in the pill.
 *
 * `not-your-turn` is the whole of it for now, and it is here rather than in a
 * mechanic's table because **everything** is turn-bound (§29 U1): construction,
 * machine policy, the mine and hand-off chips. One sentence, so the pill reads
 * the same wherever the click landed. The hover cards on the greyed controls
 * say the longer version (`selection.ts`'s `TURN_RULE`) — a pill is glanced at
 * and a card is read.
 */
export const REFUSAL = {
  "not-your-turn": "Wait for your turn.",
  // §29 U4. **It names the way out**, because the board looks perfectly
  // playable — an old position is a position, and a click on it that said only
  // "no" would leave the player hunting for what they had broken.
  "viewing-history": "You are looking at an earlier position. Press Latest to play.",
  // §31 A2: a trophy is taken on your own turn, like every action.
  "trophy-refused": "A trophy is claimed on your own turn."
} as const;

/**
 * How each refusal reads to a player.
 *
 * The *rule* is `campaign/sacrifice.ts` and the reducer's own wording is its
 * table; this is the same set said to somebody looking at a board, which is
 * the split `UPGRADE_REFUSAL` already makes beside `canUpgrade`. Every one
 * names the thing that is wrong — a control that says only "no" leaves the
 * player to guess which of six rules they broke.
 */
export const SACRIFICE_SAYS: Record<SacrificeError, string> = {
  "not-your-turn": REFUSAL["not-your-turn"],
  "no-power": "You do not have Sacrifice.",
  "in-check": "Not while you are in check — taking your own piece apart cannot answer it.",
  "no-king": "You have no king to take it apart beside.",
  "already-this-turn": "Your king has already taken something apart this turn.",
  "not-your-piece": "That piece is not yours.",
  "cannot-be-taken": "That piece cannot be taken apart.",
  "not-beside-your-altar": "It has to be standing beside your altar."
};

/**
 * The refusal, and a count of how many have been said.
 *
 * **Because the same sentence twice is two answers.** Clicking the same
 * illegal thing again is a second question, and a bare `string | null` cannot
 * express it: the value has not moved, so nothing downstream hears anything,
 * and the pill that is meant to say why sits there mid-fade looking like the
 * game did not notice. The counter is the event; the text is what it says.
 * Clearing does not bump — silence is not an announcement.
 *
 * **A reducer, so `setRefusal` is stable and the thirty-odd call sites in this
 * file are untouched.** The bump belongs to the setter rather than to every
 * place that calls it, or the next one added forgets it; and
 * `react-hooks/exhaustive-deps` knows a `useReducer` dispatch never changes,
 * where a `useCallback` handed back from a custom hook is twelve new warnings
 * on callbacks that did not move.
 */
export interface Refusal {
  text: string | null;
  said: number;
}

export const NOTHING_REFUSED: Refusal = { text: null, said: 0 };

/** The action *is* the sentence, or null to clear it. */
export function sayRefusal(was: Refusal, text: string | null): Refusal {
  if (text === null) return was.text === null ? was : { text: null, said: was.said };
  return { text, said: was.said + 1 };
}

/**
 * Why a card would not play, in words.
 *
 * Same rule as every other click in this codebase: a control that cannot mean
 * anything has to say why. Silent no-ops are how three re-aim traps survived
 * to playtest.
 */
/**
 * Why a rung or a row is refused, as a sentence.
 *
 * **Keyed by `UpgradeError`, never `string`.** `canUpgrade` and
 * `canTakeVariant` share the union, and a `Record<string, string>` let
 * `not-for-sale` arrive with no entry — `setRefusal(undefined)`, a silent
 * no-op, which is the one thing a refused click may never be. The badge's
 * own note (`badges/options.ts`) says the same refusal with the numbers in it;
 * this is the pre-flight sentence the pill says, and the type is what keeps
 * the two sets of keys in step.
 */
export const UPGRADE_REFUSAL: Record<UpgradeError, string> = {
  "not-your-piece": "Not your piece.",
  "cannot-climb": "That rank is the top of the ladder.",
  "not-that-rank": "Not the next rung up.",
  "too-poor": "No forger beside it or Armory in reach can pay the bill.",
  "already-a-variant": "That piece has already been specialised.",
  "not-for-sale": "Not for sale — a card grants that one.",
  // §31 S5: the upgrade site.
  "no-armory": "Upgrades need an Armory of yours standing.",
  "out-of-armory-reach": "Bring it within 2 of your Armory, or beside a piece of yours that forges.",
  // §31 P3: two fuel points from the source that pays.
  "no-fuel": "The source that can pay has no 2 fuel points — power in reach, a pool, or coal it holds."
};

/** `BuildError`'s words (§31 S3–S6). Typed, so a new code cannot go unsaid. */
export const BUILD_SAYS: Record<BuildError, string> = {
  "not-your-piece": "Not your piece.",
  "too-poor": "Not enough material in that piece's hands or your stores.",
  "no-room": "No piece of yours stands beside bare ground.",
  "not-beside-you": "Build beside one of your pieces.",
  "square-not-empty": "That square is not bare.",
  "needs-firm-wall": "A Reinforced Wall goes over a Firm Wall of yours, beside one of your pieces.",
  "needs-store": "A Drone-Assisted Warehouse goes over a store of yours, beside one of your pieces.",
  "no-armory": "Needs an Armory of yours standing.",
  "out-of-armory-reach": "Build it within 2 of your Armory, or from a piece that forges.",
  "needs-track": "A train goes on a Track of yours with nobody on it, beside one of your pieces.",
  "no-coupling": "A car goes right behind an engine of yours, or behind its last car (10 at most)."
};

/** `TrainError`'s words (§31 S7). */
export const TRAIN_SAYS: Record<TrainError, string> = {
  "not-your-engine": "That is not your engine.",
  "not-your-car": "That is not your car.",
  "not-your-piece": "Board your own piece.",
  "no-route": "No way there going forward along your track — a train never reverses.",
  "not-beside-car": "Board from a square touching the car.",
  royal: "A king does not ride the train.",
  held: "That piece is held in place.",
  "car-full": "That car already carries two.",
  "car-empty": "Nobody is riding in that car.",
  "loads-spent": "That car has taken two on this turn already.",
  "unloads-spent": "That car has set two down this turn already.",
  "not-orthogonal": "Riders step off onto a square straight beside the car.",
  "square-blocked": "Riders step off onto an open square."
};

export const CARD_REFUSAL: Record<string, string> = {
  "not-a-run": "Cards only exist in a run.",
  "not-in-hand": "That card is not in your hand.",
  "game-over": "The game is over.",
  "needs-a-target": "Pick a piece for that card first.",
  "not-your-piece": "That is not one of your pieces.",
  "no-piece-there": "There is nothing standing on that square.",
  "not-a-king": "Not a king's colours — there is only one of it, so nobody would believe the costume.",
  "already-armoured": "That piece is already frozen.",
  // **The Signet Crown does not crown.** It gives a queen a foundry and the
  // queen stays a queen — the crown is Coronation's word, and what it makes is
  // an Empress. Two mechanics on the same piece, so the language has to keep
  // them apart or a player reads one and gets the other. The card's own name
  // now carries a crown in it (§26 C6), which is exactly why the sentence
  // names what it *does* rather than repeating the title back.
  "not-a-queen": "The Signet Crown puts a foundry in a queen. Nothing else takes one.",
  "already-crowned": "She already forges.",
  // The Joan-Pattern row bought its reach with the factory, so the crown has
  // nothing to put a foundry in.
  "no-factory": "A Joan-Pattern Queen gave up her factory for the guns. Nothing puts it back.",
  // Coronation. Three, because each sends the player somewhere different.
  "not-a-power-piece": "Only a Power piece is crowned — not a pawn or other fodder.",
  "already-royal": "That piece is already royal.",
  "no-monarch-beside":
    "A coronation is presided over — the piece has to be standing beside a Monarch of yours.",
  // The Micro-Singularity Bomb (§26 C7).
  "not-a-square": "There is no square there to destroy.",
  "no-piece-beside": "The bomb is carried — one of your pieces has to be touching that square.",
  "never-a-monarch": "No card destroys a king.",
  "objective-square": "This level still needs that square.",
  // Programmer Oil (§31 C6): a faction card.
  "not-robotic": "That card is for the Robotic Monarchies alone.",
  // The Signet Crown pays like an upgrade (§31 C5, P3).
  "no-armory": "The Signet Crown needs an Armory of yours standing.",
  "out-of-armory-reach": "Bring the queen within 2 of your Armory, or beside a piece of yours that forges.",
  "too-poor": "Needs 3 electrum + 3 diamond from a forger beside her or an Armory in reach.",
  "no-fuel": "The source that can pay has no 2 fuel points — power in reach, a pool, or coal it holds.",
  // The Enemy is at the Gates (§31 C4).
  "no-enemies": "There is no enemy army on the board for the points to scale with.",
  // The Spatial Manifold (§31 C7): one edge, picked on the arrows.
  "needs-a-direction": "Pick an edge for the Spatial Manifold to push out.",
  "edge-of-the-world": "The board cannot reach any further that way.",
  "nothing-to-grow": "There is nowhere on that edge for the board to grow.",
  "opens-a-line": "That new ground would open a line onto your own king.",
  // Reachable only by playing the Time Fluxxer through the ordinary door, which is a
  // wiring fault rather than a move. It says what the card does instead of
  // saying no, because the one reader who ever sees it is looking for why.
  "rewind-only": "The Time Fluxxer rewinds the board, which is not something the board can do to itself."
};

/**
 * Why a rewind would not happen, in words.
 *
 * Its own table rather than rows in `CARD_REFUSAL`, because these are not
 * reasons a *card* was refused — the Time Fluxxer's own `refuse` answers `rewind-only`
 * to every play. They are facts about the log, which is a thing no other card
 * has an opinion about, plus the one row about whether the card is still yours
 * to spend, which the log is also the only witness to.
 */
export const REWIND_REFUSAL: Record<RewindRefusal, string> = {
  // Unreachable from here — `playCard` finds the card in the deck before it
  // asks for a rewind at all — and a sentence rather than a shrug because the
  // guard behind it is what stops one card paying for two rewinds.
  "card-not-held": "That card is not in your deck; it has been spent.",
  // **Not "you have not had a turn yet", which is false in the case that is
  // not turn one.** While the run is still solo the clock never leaves you —
  // `nextSide` skips a side with no pieces — so there are no boundaries to go
  // back to however many turns have been played. What the rewind needs is a
  // completed *round*, and this says so in both situations.
  "no-turn-to-take-back": "Nobody has answered a turn of yours yet, so there is nothing to take back.",
  "not-yet-earned":
    "The Time Fluxxer reached you during the round it would take back, so there is nothing to return to.",
  "rewind-refused": "That rewind was refused on the board it landed on."
};

/**
 * Why Undo is greyed, in words.
 *
 * **The reasons come from the log and from the screen**, which is why the union
 * is wider than the store's. `GameStore` can say whether there is anything to
 * take back and whether the log still replays; it cannot know that the player
 * is looking through history rather than at the live board (U4), or that the
 * socket is down so the room cannot be asked (U3). Both are facts about the
 * chrome, and both are said in the same table so the button has one voice.
 *
 * **A room is no longer a reason.** Undo online is a server operation (§29
 * U3) and the store's own answer greys it there too, the room's log being the
 * store's log and both starting at `newGame()`.
 *
 * Beside `REWIND_REFUSAL` because they are the same family: facts about the
 * log, said to somebody looking at a board.
 */
export type UndoBlocked = StoreUndoRefusal | "reconnecting";

export const UNDO_REFUSAL: Record<UndoBlocked, string> = {
  "not-your-turn": "You can only take back your own turn.",
  // **The fact and nothing else.** Two clauses were tried and both were false:
  // "you have not moved yet" on the turn whose one move ended it, and "this
  // turn is where it started" on any turn at all — the tick ran between them,
  // a wave may have landed, the board may have grown. What is true is only
  // that you have done nothing takeable.
  "nothing-to-take-back": "Nothing to take back yet.",
  // The socket is not open, so the request would answer `false` and land
  // nowhere. A click that cannot mean anything says why.
  "reconnecting": "Reconnecting to the room — try again in a moment.",
  // Undo cuts the *live* game, which is not the board in front of you (§29
  // U4). Greyed with the reason, never hidden, exactly as the other three are.
  "viewing-history": "Press Latest first — Undo takes back the live game, not the one you are looking at.",
  // Only reachable when something reached the state without going through
  // `applyCommand` — a dev hack or an e2e smoke — since a replay of a log the
  // store itself wrote cannot refuse. Said rather than thrown: the turn row
  // asks during render, and the throw blanked the page.
  "history-broken": "This game's history cannot be replayed, so nothing can be taken back.",
  // The mode's own row says so (§31.1 Q2).
  "undo-off": "Undo is off in this mode."
};

/** `BeltPlacementError`'s words (§31 S2). Typed, so a new code cannot go unsaid. */
export const PLACEMENT_REFUSAL: Record<BeltPlacementError, string> = {
  "off-board": "Off the board.",
  occupied: "Something is standing there.",
  "on-heap": "That square holds scrap — mine it first.",
  "belt-exists": "That part is your opponent's.",
  "not-beside-you": "A belt part is built beside one of your pieces.",
  "too-poor": `A belt part costs ${RULES.beltCost} cobble, from a piece beside it or your stores.`
};
